import type { DataPoint } from "~/tools/data-point";
import { isBefore, isWithinInterval } from "~/tools/date-time";
import { type Time, isTime } from "~/tools/date-time/time";
import { find } from "~/tools/find";
import { findLast } from "~/tools/find-last";
import { first } from "~/tools/first";
import { isPresent } from "~/tools/is-present";
import { last } from "~/tools/last";
import { piped } from "~/tools/piped";
import { prop } from "~/tools/prop";
import { sortedIndexWith } from "~/tools/sorted-index-with";

export const ALL_SINGLE_POINT_CALCULATIONS = ["first", "first-number", "last", "last-number"] as const;

export type SinglePointCalculation = (typeof ALL_SINGLE_POINT_CALCULATIONS)[number];

export function isSinglePointCalculation(value: string | Time): value is Time | SinglePointCalculation {
	return isTime(value) || ALL_SINGLE_POINT_CALCULATIONS.includes(value as SinglePointCalculation);
}

export function calculateSinglePoint(
	calculation: Time | SinglePointCalculation,
	points: ReadonlyArray<DataPoint>,
): undefined | DataPoint {
	switch (calculation) {
		case "first": {
			return first(points);
		}

		case "first-number": {
			return find(points, piped(prop(1), isPresent));
		}

		case "last": {
			return last(points);
		}

		case "last-number": {
			return findLast(points, piped(prop(1), isPresent));
		}

		default: {
			/**
			 * Return undefined when the calculation time is out of the points time interval
			 */
			if (
				points.length === 0 ||
				!isWithinInterval(calculation, { start: points[0][0], end: points[points.length - 1][0] })
			) {
				return undefined;
			}

			const coordIndex = sortedIndexWith(points, ([time]) => isBefore(time, calculation));

			return points[coordIndex];
		}
	}
}
